/**
 * What the concierge understood a free-text search to mean.
 *
 * Deliberately plain words, not tag ids: the portal resolves `terms` against
 * the live tag service with the same `resolveTerms` the watchlist builder
 * uses, so the model needs no tag catalog in its prompt and there is one
 * resolution path rather than two that can drift apart.
 */
export interface SearchIntent {
  schema_version: number;
  /** Companies, topics or free text. Resolved client-side to tags/tickers. */
  terms: string[];
  /** Only when the user clearly typed a symbol. */
  tickers: string[];
  limit: number;
  exact_match: boolean;
  /** How `terms` combine with each other. */
  keyword_op: "AND" | "OR";
  /** How the term group meets the ticker group. */
  filter_mode: "AND" | "OR";
  /**
   * A token, never a date. The model does not reliably know today's date and
   * date arithmetic is a common place for it to invent one, so the range is
   * computed here.
   */
  date_range: DateRangeToken;
  /**
   * One short user-facing phrase describing the interpretation.
   *
   * Shown above the results. A search that quietly misunderstood you is worse
   * than one that failed, so the reading is always on screen.
   */
  explanation: string;
}

export const DATE_RANGE_TOKENS = [
  "any",
  "today",
  "last_7_days",
  "last_30_days",
  "last_90_days",
] as const;

export type DateRangeToken = (typeof DATE_RANGE_TOKENS)[number];

export const DATE_RANGE_LABEL: Record<DateRangeToken, string> = {
  any: "any time",
  today: "today",
  last_7_days: "the last 7 days",
  last_30_days: "the last 30 days",
  last_90_days: "the last 90 days",
};
